'use client'

import { captureException } from '@sentry/browser'
import { usePathname } from 'next/navigation'
import { usePostHog } from 'posthog-js/react'
import { useEffect } from 'react'
import { useWorkspaceMember } from '@/hooks/use-permissions'
import { useUser } from '@/hooks/use-user'
import { useWorkspaceId } from '@/hooks/use-workspace'
import { isDedicatedAgentChatRoute } from '@/lib/agent/agent-chat-routes'
import { setPostHogWorkspaceContext } from '@/lib/analytics/posthog-runtime-context'

const SCHEDULING_ANALYTICS_ENRICHMENT_DELAY_MS = 60_000
const INSIGHTS_ANALYTICS_ENRICHMENT_DELAY_MS = 15_000
const NEXT_LOCALE_COOKIE_PATTERN = /(?:^|; )NEXT_LOCALE=([^;]+)/
const noop = () => undefined

// Defer non-critical enrichment until the browser is idle. PostHog already
// has user + workspace IDs from the synchronous `identify` / `group` calls;
// the role + workspace metadata only enriches dashboards and is fine to
// arrive a few seconds late. Off the critical-path saves ~5 Supabase round
// trips on every page mount (workspace_members role lookup, workspaces
// name/type, integrations, properties HEAD count, workspace_members HEAD
// count).
function deferTask(callback: () => void, delayMs = 0): () => void {
  if (typeof window === 'undefined') {
    return noop
  }

  if (delayMs > 0) {
    let cancelIdleTask: (() => void) | null = null
    const timeoutId = setTimeout(() => {
      cancelIdleTask = deferTask(callback)
    }, delayMs)
    return () => {
      clearTimeout(timeoutId)
      cancelIdleTask?.()
    }
  }

  if ('requestIdleCallback' in window && 'cancelIdleCallback' in window) {
    const id = window.requestIdleCallback(callback, { timeout: 4000 })
    return () => window.cancelIdleCallback(id)
  }
  const id = setTimeout(callback, 2000)
  return () => clearTimeout(id)
}

/**
 * Identifies the current user and workspace in PostHog so events, replays,
 * and feature-flag evaluations are tied to a stable distinct_id and tenant
 * group. Mirrors SentryUserIdentity but for PostHog.
 *
 * Group analytics: every authenticated request is grouped by workspace so
 * dashboards can slice by tenant ("retention for haven-vacation-rentals",
 * "feature usage for renjoy", etc.) without per-user noise.
 */
export function PostHogUserIdentity() {
  const { data: user } = useUser()
  const workspaceId = useWorkspaceId()
  const pathname = usePathname()
  const isAgentChatRoute = isDedicatedAgentChatRoute(pathname, workspaceId ?? '')
  const { data: member } = useWorkspaceMember({ enabled: !isAgentChatRoute })
  const hasConfirmedWorkspaceMember = !isAgentChatRoute && Boolean(member)
  const posthog = usePostHog()
  let enrichmentDelayMs = 0
  if (pathname.includes('/tasks/scheduling')) {
    enrichmentDelayMs = SCHEDULING_ANALYTICS_ENRICHMENT_DELAY_MS
  } else if (pathname.includes('/insights')) {
    enrichmentDelayMs = INSIGHTS_ANALYTICS_ENRICHMENT_DELAY_MS
  }

  useEffect(() => {
    const verifiedWorkspaceId =
      user && workspaceId && hasConfirmedWorkspaceMember ? workspaceId : null
    setPostHogWorkspaceContext(verifiedWorkspaceId)
    return () => setPostHogWorkspaceContext(null)
  }, [user, workspaceId, hasConfirmedWorkspaceMember])

  useEffect(() => {
    if (!posthog) {
      return
    }

    if (user) {
      posthog.identify(user.id, {
        email: user.email ?? undefined,
        name: user.full_name ?? user.email ?? undefined,
        // Auth-level fields available without a roundtrip — useful for
        // cohorts like "users who signed up after 2026-01-01" and
        // funnels gated on email_confirmed.
        created_at: user.created_at ?? undefined,
        last_sign_in_at: user.last_sign_in_at ?? undefined,
        email_confirmed_at: user.email_confirmed_at ?? undefined,
        avatar_url: user.avatar_url ?? undefined,
        // Locale comes from the next-intl cookie; PostHog's internal
        // $browser_language is auto-detected but doesn't account for the
        // explicit i18n preference the user picked in settings.
        locale: typeof document === 'undefined' ? undefined : readNextIntlLocale(),
      })

      // Workspace-member role enrichment (admin / member / vendor / etc.)
      // requires a DB lookup — fire-and-forget, deferred to browser idle so
      // it doesn't compete with the page's critical fetches on cold load.
      if (workspaceId && hasConfirmedWorkspaceMember) {
        const controller = new AbortController()
        const cancel = deferTask(async () => {
          await enrichUserRole(posthog, user.id, workspaceId, controller.signal)
        }, enrichmentDelayMs)
        return () => {
          cancel()
          controller.abort()
        }
      }
    } else if (user === null) {
      // Only reset on confirmed sign-out. `useUser()` returns `undefined`
      // while the auth query is in flight; resetting on undefined would
      // discard the in-progress anonymous session on every page load.
      posthog.reset()
    }
  }, [user, posthog, workspaceId, hasConfirmedWorkspaceMember, enrichmentDelayMs])

  useEffect(() => {
    if (!posthog) {
      return
    }
    posthog.resetGroups()
    if (!(workspaceId && user && hasConfirmedWorkspaceMember)) {
      return
    }
    posthog.group('workspace', workspaceId)

    // Enrich the workspace group with metadata so PostHog dashboards can slice
    // tenants by plan, integration mix, and entity counts without a backend
    // round-trip on every event. groupIdentify writes once per workspace and
    // PostHog persists the props on the group object — deferring is safe.
    const controller = new AbortController()
    const cancel = deferTask(async () => {
      await enrichWorkspaceGroup(posthog, workspaceId, controller.signal)
    }, enrichmentDelayMs)
    return () => {
      cancel()
      controller.abort()
    }
  }, [workspaceId, posthog, user, hasConfirmedWorkspaceMember, enrichmentDelayMs])

  return null
}

function readNextIntlLocale(): string | undefined {
  // next-intl stores locale in the NEXT_LOCALE cookie. Reading it here
  // beats parsing navigator.language, which doesn't reflect the user's
  // explicit pick from the language switcher.
  const match = document.cookie.match(NEXT_LOCALE_COOKIE_PATTERN)
  return match?.[1]
}

interface AnalyticsContext {
  integrations: string[]
  member: {
    role?: string | null
    created_at?: string | null
    is_active?: boolean | null
  }
  members_count: number
  properties_count: number
  workspace: {
    name: string
    type: string
    created_at?: string | null
  }
}

const analyticsContextRequests = new Map<string, Promise<AnalyticsContext | null>>()

async function fetchAnalyticsContext(
  workspaceId: string,
  signal: AbortSignal
): Promise<AnalyticsContext | null> {
  if (signal.aborted) {
    return null
  }

  const existing = analyticsContextRequests.get(workspaceId)
  if (existing) {
    const data = await existing
    return signal.aborted ? null : data
  }

  const request = fetch(`/api/workspaces/${workspaceId}/analytics-context`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then((response) => {
      if (!response.ok) {
        return null
      }
      return response.json()
    })
    .finally(() => {
      analyticsContextRequests.delete(workspaceId)
    })

  analyticsContextRequests.set(workspaceId, request)
  const data = await request
  return signal.aborted ? null : data
}

function isExpectedAnalyticsFetchInterruption(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return true
  }
  return err instanceof TypeError && err.message === 'Failed to fetch'
}

async function enrichUserRole(
  posthog: ReturnType<typeof usePostHog>,
  userId: string,
  workspaceId: string,
  signal: AbortSignal
): Promise<void> {
  if (!posthog) {
    return
  }
  try {
    const data = await fetchAnalyticsContext(workspaceId, signal)
    if (!data?.member) {
      return
    }
    posthog.setPersonProperties({
      role: data.member.role ?? undefined,
      member_since: data.member.created_at ?? undefined,
      is_active_member: data.member.is_active ?? undefined,
    })
  } catch (err) {
    if (isExpectedAnalyticsFetchInterruption(err)) {
      return
    }
    captureException(err, {
      tags: { area: 'posthog', endpoint: 'enrich_user_role', workspaceId },
      extra: { userId },
    })
  }
}

async function enrichWorkspaceGroup(
  posthog: ReturnType<typeof usePostHog>,
  workspaceId: string,
  signal: AbortSignal
): Promise<void> {
  if (!posthog) {
    return
  }
  try {
    const data = await fetchAnalyticsContext(workspaceId, signal)
    if (!data) {
      return
    }

    posthog.group('workspace', workspaceId, {
      name: data.workspace.name,
      type: data.workspace.type,
      created_at: data.workspace.created_at ?? undefined,
      properties_count: data.properties_count,
      members_count: data.members_count,
      integrations: data.integrations,
    })
  } catch (err) {
    if (isExpectedAnalyticsFetchInterruption(err)) {
      return
    }
    captureException(err, {
      tags: { area: 'posthog', endpoint: 'enrich_workspace_group', workspaceId },
    })
  }
}
